import { Link } from 'react-router-dom';
import { BusinessType } from '../../../../data-layer/business/types';
import { BusinessRegistrationStatus } from '../../../../mappers/users/toUserModel';
import { textDictionary } from '../../../../ui/business/BusinessRegistrationForm/constants';
import { IconCheckMarkCircle } from '../../../../ui/icon/IconCheckMarkCircle';
import { IconClock } from '../../../../ui/icon/IconClock';
import { IconCopy } from '../../../../ui/icon/IconCopy';
import { IconKey } from '../../../../ui/icon/IconKey';
import { IconPause } from '../../../../ui/icon/IconPause';
import { Flex } from '../../../../ui/layout/Flex';
import { Typography } from '../../../../ui/typography/Typography';
import { useTranslation } from '../../../../utils/useTranslation';

export const getRegistrationStatusProps = ({
    status,
    login,
    onCopy,
    businessType,
    registrationTgCode,
}: {
    status?: BusinessRegistrationStatus;
    login?: string;
    onCopy?: () => Promise<void>;
    businessType: BusinessType;
    registrationTgCode?: string;
}) => {
    const t = useTranslation();
    const textMap = textDictionary[businessType];

    if (status === BusinessRegistrationStatus.VERIFICATION) {
        return {
            iconSlot: <IconKey size={64} color="var(--color-primary)" />,
            title: t('Подтверждение доступа'),
            messageSlot: registrationTgCode ? (
                <Flex>
                    <Typography pb={0.5}>{t(textMap.socialAccountTgConfirm)}</Typography>
                    <Typography pb={0.5} mt={2}>
                        {t(
                            'Для этого необходимо добавить указанный ниже код в описание профиля. После прохождения проверки код можно будет удалить из описания профиля.',
                        )}
                    </Typography>
                    <Flex
                        p={2}
                        mt={2}
                        width="100%"
                        direction="row"
                        backgroundColor="var(--background-secondary)"
                        borderRadius="16px"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography>{registrationTgCode}</Typography>
                        <IconCopy
                            onClick={onCopy}
                            size={24}
                            cursor="pointer"
                            color="var(--color-primary)"
                        />
                    </Flex>
                    <Typography mt={2}>
                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={
                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${login}` ||
                                ''
                            }
                            color="link"
                            textDecoration="none"
                        >
                            {t('поддержку')}.
                        </Typography>
                    </Typography>
                </Flex>
            ) : (
                <Flex>
                    <Typography pb={0.5}>{t(textMap.socialAccountInstagramConfirm)}</Typography>
                    <Typography pb={0.5}>
                        {t('Для этого нужно написать в сообщения в Instagram* нашему профилю ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={'https://www.instagram.com/yandex.rythm/'}
                            color="link"
                            target="_blank"
                            textDecoration="none"
                        >
                            {t('@yandex.rythm')}
                        </Typography>
                        {t(' от имени логина, с которого вы создали данную заявку.')}
                    </Typography>
                    <Typography pb={2}>{t('Текст сообщения: ')}</Typography>
                    <Flex
                        p={2}
                        direction="row"
                        backgroundColor="var(--background-secondary)"
                        borderRadius="16px"
                        alignItems="center"
                    >
                        <Typography>
                            {t(`${login} ${textMap.accountAccessVerification}`)}
                        </Typography>
                        <IconCopy
                            onClick={onCopy}
                            size={24}
                            cursor="pointer"
                            color="var(--color-primary)"
                        />
                    </Flex>
                    <Typography pt={2} pb={0.5}>
                        {t(textMap.verificationDisclaimer)}
                    </Typography>
                    <Typography pb={0.5}>
                        {t(
                            'Если вы не указывали Instagram*, то ничего подтверждать не нужно, мы просто вас проверим и сообщим о решении.',
                        )}
                    </Typography>
                    <Typography>
                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={
                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${login}` ||
                                ''
                            }
                            color="link"
                            textDecoration="none"
                        >
                            {t('поддержку')}.
                        </Typography>
                    </Typography>
                </Flex>
            ),
            helpSlot: registrationTgCode ? undefined : (
                <Typography px={2} color="secondary" variant="caption">
                    {t(
                        '*Meta, владеющая Instagram, признана экстремистской организацией на территории РФ',
                    )}
                </Typography>
            ),
        };
    }
    if (status === BusinessRegistrationStatus.MODERATION) {
        return {
            iconSlot: <IconClock size={64} color="var(--color-primary)" />,
            title: t('Проверяем аккаунт'),
            messageSlot: (
                <Flex>
                    <Typography pb={0.5}>
                        {t('Нам нужно немного времени, мы свяжемся с вами позже.')}
                    </Typography>
                    <Typography>
                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={
                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${login}` ||
                                ''
                            }
                            color="link"
                            textDecoration="none"
                        >
                            {t('поддержку')}.
                        </Typography>
                    </Typography>
                </Flex>
            ),
        };
    }
    if (status === BusinessRegistrationStatus.DECLINED_FOR_BETA) {
        return {
            iconSlot: <IconPause size={64} color="var(--color-primary)" />,
            title: t('Пока не можем вас подключить'),
            messageSlot: (
                <Flex>
                    <Typography pb={0.5}>{t(textMap.declinedForBetaStatus)}</Typography>
                    <Typography>
                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={
                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${login}` ||
                                ''
                            }
                            color="link"
                            textDecoration="none"
                        >
                            {t('поддержку')}.
                        </Typography>
                    </Typography>
                </Flex>
            ),
        };
    }
    if (status === BusinessRegistrationStatus.REGISTERED) {
        return {
            iconSlot: <IconCheckMarkCircle size={64} color="var(--color-primary)" />,
            title: textMap.accountRegistered,
            messageSlot: (
                <Flex>
                    <Typography pb={0.5}>
                        {t('Теперь вы можете перейти в профиль и настроить его')}
                    </Typography>
                </Flex>
            ),
        };
    }
    if (status === BusinessRegistrationStatus.MULTIPLE_ROLE_RESTRICTED) {
        return {
            iconSlot: <IconPause size={64} color="var(--color-primary)" />,
            title: t('Пока не можем вас подключить'),
            messageSlot: (
                <Flex>
                    <Typography pb={0.5}>
                        {t('В данный момент нельзя одновременно быть автором и владельцем бизнеса')}
                    </Typography>
                    <Typography>
                        {t('Если возникли вопросы, вы всегда можете обратиться в ')}
                        <Typography
                            display="inline"
                            as={Link}
                            to={
                                `${process.env.REACT_APP_BETA_SUPPORT_FORM_LINK}?login=${login}` ||
                                ''
                            }
                            color="link"
                            textDecoration="none"
                        >
                            {t('поддержку')}.
                        </Typography>
                    </Typography>
                </Flex>
            ),
        };
    }
};
